import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AdminDataTableComponent } from '../../../shared/components/admin-data-table/admin-data-table.component';
import type { AdminTableColumn } from '../../../shared/components/admin-data-table/admin-table-column.interface';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { CtaButtonComponent } from '../../../shared/components/ui/cta-button/cta-button.component';
import { CourseCreatorComponent } from '../../courses/components/course-creator/course-creator.component';
import { COURSE_CREATOR_BRIDGE } from '../../courses/components/course-creator/bridges/course-creator-bridge.token';
import { AdminValvurCourseCreatorBridge } from '../../courses/components/course-creator/bridges/admin-valvur-course-creator.bridge';
import { DEFAULT_COURSE_CREATOR_STEPS } from '../../courses/models';
import { AdminContentService } from './services/content.service';
import { CourseService } from '../../courses/services/course.service';
import { challenge as ChallengeGen } from '@valvur/shared-grpc';
import { AdminPageShellComponent } from '../../../shared/components/admin/admin-page-shell/admin-page-shell.component';
import { CourseCardComponent } from './components/course-card/course-card.component';
import { MatDialog } from '@angular/material/dialog';
import { CourseEditModalComponent } from './components/course-edit-modal/course-edit-modal.component';
import { COURSE_EDIT_DIALOG_PANEL_CLASS } from '../../../shared/constants/parent-dialog.constants';

@Component({
  selector: 'app-content-page',
  standalone: true,
  providers: [
    {
      provide: COURSE_CREATOR_BRIDGE,
      useClass: AdminValvurCourseCreatorBridge,
    },
  ],
  imports: [
    TranslatePipe,
    MatProgressSpinnerModule,
    AdminDataTableComponent,
    ModalComponent,
    CtaButtonComponent,
    CourseCreatorComponent,
    AdminPageShellComponent,
    CourseCardComponent,
  ],
  templateUrl: './content-page.component.html',
  styleUrl: './content-page.component.scss',
})
export class ContentPageComponent {
  /** Admin course creator: same as school flow but without the Assign step. */
  readonly courseCreatorSteps = DEFAULT_COURSE_CREATOR_STEPS.filter(
    (s) => s.id !== 'assign',
  );

  readonly #content = inject(AdminContentService);
  readonly #courseService = inject(CourseService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #translate = inject(TranslateService);
  readonly #dialog = inject(MatDialog);

  readonly isCourseModalOpen = signal(false);
  readonly selectedCourseForCreator = signal<ChallengeGen.Course | undefined>(
    undefined,
  );
  readonly courseCreatorModalStep = signal<string>('settings');

  constructor() {
    this.#content.isAvailableCoursesQueryEnabled.set(true);
    this.#courseService.adminValvurMutationIsolation.set(true);
    this.#destroyRef.onDestroy(() => {
      this.#content.isAvailableCoursesQueryEnabled.set(false);
      this.#courseService.adminValvurMutationIsolation.set(false);
    });

    effect(() => {
      const sel = this.selectedCourseForCreator();
      const list = this.#content.availableCourses();
      if (!sel || !list?.length) return;
      const updated = list.find((c) => c.id === sel.id);
      if (updated && updated !== sel) {
        this.selectedCourseForCreator.set(updated);
      }
    });
  }

  /** Courses from GET /courses/valvur (admin VALVUR catalog). */
  readonly courses = this.#content.availableCourses;

  readonly isLoading = computed(() =>
    this.#content.valvurCoursesQuery.isLoading(),
  );
  readonly hasCourses = computed(() => this.courses().length > 0);

  readonly columns: AdminTableColumn[] = [
    {
      id: 'courseImage',
      headerKey: 'app.admin.contentPage.columns.courseImage',
      thClass: 'col-hero col-center',
    },
    {
      id: 'name',
      headerKey: 'app.admin.contentPage.columns.courseName',
      thClass: 'col-center',
    },
    {
      id: 'category',
      headerKey: 'app.admin.contentPage.columns.category',
      thClass: 'col-center',
    },
    {
      id: 'status',
      headerKey: 'app.admin.contentPage.columns.status',
      thClass: 'col-center',
    },
    {
      id: 'duration',
      headerKey: 'app.admin.contentPage.columns.duration',
      thClass: 'col-center',
    },
    {
      id: 'source',
      headerKey: 'app.admin.contentPage.columns.source',
      thClass: 'col-center',
    },
    {
      id: 'actions',
      headerKey: 'app.admin.contentPage.columns.actions',
      thClass: 'col-center col-actions',
    },
  ];

  readonly trackCourse = (row: ChallengeGen.Course): string => row.id;

  /** Signed URLs may expire; hide broken img and show spinner while still pre-publish. */
  readonly brokenCourseImageIds = signal(new Set<string>());

  onCourseImageError = (courseId: string): void => {
    this.brokenCourseImageIds.update((prev) => {
      const next = new Set(prev);
      next.add(courseId);
      return next;
    });
  };

  /** Cover not ready yet or failed to load while course is still being prepared. */
  readonly showCourseHeroSpinner = (course: ChallengeGen.Course): boolean => {
    const prePublish =
      course.status === 'DRAFT' ||
      course.status === 'READY_FOR_VIDEO_GENERATION';
    if (!prePublish) return false;
    const noUrl = !course.courseImageUrl?.trim();
    const broken = this.brokenCourseImageIds().has(course.id);
    return noUrl || broken;
  };

  readonly hasCourseHeroImage = (course: ChallengeGen.Course): boolean => {
    const url = course.courseImageUrl?.trim();
    if (!url) return false;
    return !this.brokenCourseImageIds().has(course.id);
  };

  readonly statusLabel = (status: string): string => {
    const key = `app.admin.contentPage.status.${status}`;
    const t = this.#translate.instant(key);
    return t !== key ? t : status;
  };

  readonly sourceLabel = (source: string): string => {
    const key = `app.admin.contentPage.source.${source}`;
    const t = this.#translate.instant(key);
    return t !== key ? t : source;
  };

  /** Opens course creator on the settings step with no course (create flow). */
  openNewCourseModal(): void {
    this.selectedCourseForCreator.set(undefined);
    this.courseCreatorModalStep.set('settings');
    this.isCourseModalOpen.set(true);
  }

  openCourseInEditor(course: ChallengeGen.Course): void {
    if (course.status === 'DRAFT') {
      this.selectedCourseForCreator.set(course);
      this.courseCreatorModalStep.set('generate');
      this.isCourseModalOpen.set(true);
      return;
    }

    this.#dialog
      .open(CourseEditModalComponent, {
        data: { course },
        panelClass: COURSE_EDIT_DIALOG_PANEL_CLASS,
        autoFocus: 'first-timer',
        restoreFocus: true,
      })
      .afterClosed()
      .subscribe(() => {
        void this.#content.valvurCoursesQuery.refetch();
      });
  }

  onCourseModalClosed(): void {
    this.isCourseModalOpen.set(false);
    this.selectedCourseForCreator.set(undefined);
    this.brokenCourseImageIds.set(new Set());
    void this.#content.valvurCoursesQuery.refetch();
  }
}
